import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useGetContributorsQuery } from "../../services/rtkApi/artistsGalleryApi";

interface Props {
  // when set, that contributor is excluded — main gallery title shows them,
  // so the strip on their own page lists "the rest"
  activeUsername?: string;
}

// inline strip: "hosted contributors: <emoji>.<username> | <emoji>.<username> | …"
export const ContributorsStrip: React.FC<Props> = ({ activeUsername }) => {
  const { data: contributors = [], isLoading } = useGetContributorsQuery();

  const others = activeUsername
    ? contributors.filter((c) => c.username !== activeUsername)
    : contributors;

  if (isLoading) {
    return (
      <Strip>
        <Label>hosted contributors:</Label>
        <Note>loading…</Note>
      </Strip>
    );
  }

  if (others.length === 0) return null;

  return (
    <Strip>
      <Label>hosted contributors:</Label>
      <List>
        {others.map((c, idx) => {
          const handle = `${c.userEmoji ? `${c.userEmoji}.` : ""}${c.username}`;
          return (
            <React.Fragment key={c._id}>
              {idx > 0 && <Sep aria-hidden>|</Sep>}
              <Tile
                to={`/art/${encodeURIComponent(c.username)}`}
                title={c.displayName || c.username}
              >
                {handle}
              </Tile>
            </React.Fragment>
          );
        })}
      </List>
    </Strip>
  );
};

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
`;

const Label = styled.span`
  opacity: 0.75;
`;

const List = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
`;

const Sep = styled.span`
  opacity: 0.5;
  user-select: none;
`;

const Note = styled.span`
  opacity: 0.65;
`;

const Tile = styled(Link)`
  font-family: inherit;
  color: #000000;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;

  &:hover {
    color: #000000;
    text-decoration-thickness: 2px;
  }
`;

export default ContributorsStrip;
